/**
 * SkipLink - Skip to main content link for keyboard accessibility
 *
 * WCAG 2.1 AA Success Criterion 2.4.1 (Bypass Blocks)
 * Israeli accessibility standard SI 5568
 *
 * Features:
 * - Visually hidden until focused
 * - Appears at top-left on focus
 * - Moves focus to main content area
 */

import React from 'react';
import { cn } from '../../lib/utils';

interface SkipLinkProps {
  /** ID of the target main content element */
  mainContentId?: string;
  /** Custom className */
  className?: string;
}

const SkipLink: React.FC<SkipLinkProps> = ({
  mainContentId = 'main-content',
  className,
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(mainContentId);
    if (target) {
      target.tabIndex = -1;
      target.focus();
      // Remove tabIndex after focus so it doesn't affect normal tab order
      target.removeAttribute('tabindex');
    }
  };

  return (
    <a
      href={`#${mainContentId}`}
      onClick={handleClick}
      className={cn(
        // Visually hidden by default - appears on focus
        'fixed top-0 left-0 z-[100]',
        // Ensure high z-index appears above everything
        // Padding for touch target (44px minimum per WCAG)
        'py-3 px-6',
        // Background and text for visibility when focused
        'bg-accent-primary text-bg-primary font-semibold',
        // Transform to slide in from top when focused
        '-translate-y-full',
        'focus:translate-y-0',
        // Smooth transition for the slide effect
        'transition-transform duration-200',
        // Ensure the link is focusable
        'focus:outline-none focus:ring-2 focus:ring-accent-secondary',
        // Make sure it works with screen readers
        'sr-only focus:not-sr-only',
        className
      )}
      // Screen reader text
      aria-label="Skip to main content"
    >
      Skip to main content
    </a>
  );
};

export default SkipLink;